import { getAgingBucket, getDaysOverdue, getDueDateStatus, DueStatus } from '../../utils/formatters';

export interface BoletoItem {
  id: number;
  code: string;
  clientId: number;
  clientDisplayName?: string;
  clientName: string;
  clientTradeName?: string;
  clientCnpj?: string;
  referenceMonth: string;
  dueDate: string;
  totalValue: number;
  nossoNumero: string | null;
  boletoStatus: string | null;
  boletoPaymentDate: string | null;
  boletoPaidValue: number | null;
  jurosMulta?: number | null;
  status: string;
  boletoHistory?: any[] | null;
  hibrido?: boolean;
}

/** Estados em que o título está vivo no banco. */
const ATIVOS = ['registrado', 'confirmado', 'baixa_solicitada', 'pago'];
const EMISSAO_TRAVADA = 'emissao_em_andamento';

export type AttentionReason =
  | 'emissao_travada'
  | 'paga_com_titulo_vivo'
  | 'aberta_sem_instrumento'
  | 'cancelada_com_titulo';

export interface BoletoCapabilities {
  /** Título vivo no banco (registrado, confirmado, baixa solicitada, pago): o vencimento dele não muda pela fatura. */
  temTituloVivo: boolean;
  canEmitirHibrido: boolean;
  canBaixarApi: boolean;
  canRemessaBaixa: boolean;
  canDownloadPdf: boolean;
  isSelectable: boolean;
  isOverdue: boolean;
  dueStatus: DueStatus;
  daysOverdue: number;
  agingBucket: '1-7' | '8-30' | '30+' | null;
  /** Exige ação humana — alimenta o card "Atenção". */
  needsAttention: boolean;
  attentionReason: AttentionReason | null;
  attentionLabel: string | null;
}

/**
 * Fonte ÚNICA de elegibilidade e situação de um boleto.
 *
 * Antes a mesma regra estava reescrita em cinco pontos do BoletosTab (lista de
 * elegíveis para remessa, elegíveis para baixa, checkbox da linha e os dois
 * botões de ação), com expressões ligeiramente diferentes — foi daí que veio o
 * "selecionar todos" que nunca marcava e o estado `baixado` inalcançável por
 * filtro. Qualquer mudança de status agora se propaga para todos os pontos.
 *
 * EMISSÃO POR REMESSA CNAB SAIU DA TELA (26/08/2026). Toda emissão é pela API
 * do Sicredi (`canEmitirHibrido`), que registra na hora e já devolve QR PIX +
 * linha digitável. A remessa de entrada gerava um `nossoNumero` da faixa CNAB e
 * marcava a fatura como `registrado` sem que o título existisse no banco — quem
 * esquecesse de subir o arquivo no portal ficava com uma fatura que parecia
 * emitida e um cliente sem como pagar. O endpoint `POST /cnab/remessa` continua
 * no backend como contingência (API do Sicredi fora), exigindo `contingencia:
 * true` e perfil ADMIN.
 */
export function boletoCapabilities(b: BoletoItem): BoletoCapabilities {
  const st = String(b.boletoStatus || '').trim().toLowerCase();
  const temTituloVivo = !!b.nossoNumero && ATIVOS.includes(st);
  const travada = st === EMISSAO_TRAVADA;
  const paga = b.status === 'Paga';
  const cancelada = b.status === 'Cancelada';
  const aberta = b.status === 'Aberta';

  const dueStatus = paga ? 'ok' : getDueDateStatus(b.dueDate);
  const daysOverdue = paga ? 0 : getDaysOverdue(b.dueDate);
  const isOverdue = !paga && !cancelada && dueStatus === 'overdue';

  // Situações que representam dinheiro parado ou cobrança indevida e que hoje
  // não aparecem em lugar nenhum da tela.
  let attentionReason: AttentionReason | null = null;
  if (travada) attentionReason = 'emissao_travada';
  else if (paga && !!b.nossoNumero && ['registrado', 'confirmado', 'baixa_solicitada'].includes(st))
    attentionReason = 'paga_com_titulo_vivo';
  else if (cancelada && temTituloVivo) attentionReason = 'cancelada_com_titulo';
  else if (aberta && st === 'baixado') attentionReason = 'aberta_sem_instrumento';

  const attentionLabel = attentionReason
    ? {
        emissao_travada: 'Emissão travada — confira o título no portal do Sicredi antes de reemitir',
        paga_com_titulo_vivo: 'Fatura paga com título ainda ativo no banco — risco de cobrança em duplicidade',
        cancelada_com_titulo: 'Fatura cancelada com título ativo no banco — solicite a baixa',
        aberta_sem_instrumento: 'Boleto baixado e fatura em aberto — o cliente não tem como pagar',
      }[attentionReason]
    : null;

  return {
    temTituloVivo,
    // Emitir exige fatura Aberta, sem título vivo e com vencimento no futuro —
    // o mesmo gate do backend. Antes o botão aparecia para faturas vencidas e
    // só falhava depois do clique; o caminho certo é alterar o vencimento e
    // então emitir.
    canEmitirHibrido: aberta && !temTituloVivo && !travada && !b.boletoPaymentDate && daysOverdue <= 0,
    canBaixarApi: !!b.nossoNumero && ['registrado', 'confirmado'].includes(st),
    canRemessaBaixa: !!b.nossoNumero && ['registrado', 'confirmado'].includes(st),
    canDownloadPdf: !!b.nossoNumero,
    // Selecionável = alvo de baixa em lote. O ramo "aberta sem título" saiu
    // junto com a remessa de entrada: emitir é sempre pela API agora.
    isSelectable: !!b.nossoNumero && ['registrado', 'confirmado'].includes(st),
    isOverdue,
    dueStatus,
    daysOverdue,
    agingBucket: isOverdue ? getAgingBucket(b.dueDate) : null,
    needsAttention: attentionReason !== null,
    attentionReason,
    attentionLabel,
  };
}

export type BoletoCardFilter =
  | 'all'
  | 'a_emitir'
  | 'em_aberto'
  | 'vencidos'
  | 'pagos'
  | 'atencao';

export interface BoletoStats {
  total: number;
  aEmitir: { count: number; value: number };
  emAberto: { count: number; value: number };
  vencidos: { count: number; value: number; ate7: number; ate30: number; mais30: number };
  pagos: { count: number; value: number; juros: number };
  atencao: { count: number; value: number };
}

/**
 * Todas as estatísticas numa passagem só. Antes eram oito varreduras do array
 * fora de `useMemo`, refeitas a cada render (inclusive quando só mudava uma
 * mensagem de status).
 */
export function computeStats(boletos: BoletoItem[]): BoletoStats {
  const s: BoletoStats = {
    total: boletos.length,
    aEmitir: { count: 0, value: 0 },
    emAberto: { count: 0, value: 0 },
    vencidos: { count: 0, value: 0, ate7: 0, ate30: 0, mais30: 0 },
    pagos: { count: 0, value: 0, juros: 0 },
    atencao: { count: 0, value: 0 },
  };

  for (const b of boletos) {
    const cap = boletoCapabilities(b);
    const valor = Number(b.totalValue) || 0;
    const st = String(b.boletoStatus || '').trim().toLowerCase();

    if (cap.needsAttention) {
      s.atencao.count++;
      s.atencao.value += valor;
    }
    if (b.status === 'Paga' || st === 'pago') {
      s.pagos.count++;
      s.pagos.value += Number(b.boletoPaidValue) || valor;
      s.pagos.juros += Number(b.jurosMulta) || 0;
      continue;
    }
    if (cap.canEmitirHibrido) {
      s.aEmitir.count++;
      s.aEmitir.value += valor;
    }
    if (cap.isOverdue) {
      s.vencidos.count++;
      s.vencidos.value += valor;
      if (cap.agingBucket === '1-7') s.vencidos.ate7++;
      else if (cap.agingBucket === '8-30') s.vencidos.ate30++;
      else s.vencidos.mais30++;
    } else if (!!b.nossoNumero && ['registrado', 'confirmado'].includes(st)) {
      s.emAberto.count++;
      s.emAberto.value += valor;
    }
  }
  return s;
}

/** Um boleto pertence ao filtro selecionado? */
export function matchesCardFilter(b: BoletoItem, filtro: BoletoCardFilter): boolean {
  if (filtro === 'all') return true;
  const cap = boletoCapabilities(b);
  const st = String(b.boletoStatus || '').trim().toLowerCase();
  switch (filtro) {
    case 'a_emitir':
      return cap.canEmitirHibrido;
    case 'em_aberto':
      return !cap.isOverdue && !!b.nossoNumero && ['registrado', 'confirmado'].includes(st) && b.status !== 'Paga';
    case 'vencidos':
      return cap.isOverdue;
    case 'pagos':
      return b.status === 'Paga' || st === 'pago';
    case 'atencao':
      return cap.needsAttention;
    default:
      return true;
  }
}

/**
 * E2 (12/10/2026): a mensagem da baixa em lote com falha PARCIAL. Warning e fixa; uma linha por título que falhou, com
 * o código da fatura, o nosso número e o motivo do banco. O backend devolve só `invoiceId` na falha; código e nosso
 * número saem da lista da tela (o título fora dela aparece como "fatura #id").
 */
export function fraseDaBaixaParcial(
  r: { total: number; baixados: unknown[]; falhas: { invoiceId: number; erro: string }[] },
  lista: BoletoItem[],
): { type: 'warning'; text: string; fixa: true } {
  const porId = new Map(lista.map((b) => [b.id, b]));
  const linhas = r.falhas.map((f) => {
    const b = porId.get(f.invoiceId);
    const quem = b ? `${b.code}${b.nossoNumero ? ` (nosso número ${b.nossoNumero})` : ''}` : `fatura #${f.invoiceId}`;
    return `• ${quem}: ${f.erro}`;
  });
  const n = r.falhas.length;
  return {
    type: 'warning',
    fixa: true,
    text:
      `${r.baixados.length} de ${r.total} título(s) baixado(s) no Sicredi. ${n === 1 ? 'Este não foi baixado e segue marcado' : `Estes ${n} não foram baixados e seguem marcados`} para tentar de novo:\n` +
      linhas.join('\n'),
  };
}
